import { Events } from './events';
import { withProvenance } from './provenance';

type TourStepBase = {
    transitionDuration: number;
    pauseDuration: number;
    layerStateOverride?: Record<string, boolean>;
    label?: string;
    narrationNote?: string;
};

type TourStepBookmark = TourStepBase & {
    type: 'bookmark';
    bookmarkId: string;
    measurementVisibleIds?: string[];
    bookmarkVisibleIds?: string[];
};

type TourStepCustomView = TourStepBase & {
    type: 'customView';
    cameraPose: {
        position: [number, number, number];
        target: [number, number, number];
        fov?: number;
    };
    measurementVisibleIds?: string[];
    bookmarkVisibleIds?: string[];
};

type TourStep = TourStepBookmark | TourStepCustomView;

type Tour = {
    id: string;
    name: string;
    steps: TourStep[];
    author?: string;
    createdAt: string;
};

type TourUpdatePayload = {
    id: string;
    patch: Partial<Omit<Tour, 'id'>>;
};

const isFiniteNumber = (value: unknown): value is number => {
    return typeof value === 'number' && Number.isFinite(value);
};

const sanitizeVec3Tuple = (value: unknown): [number, number, number] | null => {
    if (!Array.isArray(value) || value.length !== 3) {
        return null;
    }

    const [x, y, z] = value;
    if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(z)) {
        return null;
    }

    return [x, y, z];
};

const sanitizeLayerStateOverride = (value: unknown): Record<string, boolean> | undefined => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return undefined;
    }

    const next: Record<string, boolean> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (typeof key === 'string' && typeof entry === 'boolean') {
            next[key] = entry;
        }
    }

    return Object.keys(next).length > 0 ? next : undefined;
};

const sanitizeDuration = (value: unknown): number | null => {
    if (!isFiniteNumber(value) || value < 0) {
        return null;
    }
    return value;
};

const sanitizeIdList = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }

    const filtered = value.filter((item): item is string => typeof item === 'string' && item.length > 0);
    return filtered;
};

const sanitizeTourStepBase = (value: Record<string, unknown>): TourStepBase | null => {
    const transitionDuration = sanitizeDuration(value.transitionDuration);
    const pauseDuration = sanitizeDuration(value.pauseDuration);

    if (transitionDuration === null || pauseDuration === null) {
        return null;
    }

    const sanitized: TourStepBase = {
        transitionDuration,
        pauseDuration
    };

    const layerStateOverride = sanitizeLayerStateOverride(value.layerStateOverride);
    if (layerStateOverride) {
        sanitized.layerStateOverride = layerStateOverride;
    }

    if (typeof value.label === 'string') {
        sanitized.label = value.label;
    }

    if (typeof value.narrationNote === 'string') {
        sanitized.narrationNote = value.narrationNote;
    }

    return sanitized;
};

const sanitizeTourStep = (value: unknown): TourStep | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    const base = sanitizeTourStepBase(candidate);
    if (!base) {
        return null;
    }

    if (candidate.type === 'bookmark') {
        if (typeof candidate.bookmarkId !== 'string' || candidate.bookmarkId.length === 0) {
            return null;
        }

        const measurementVisibleIds = sanitizeIdList(candidate.measurementVisibleIds);
        const bookmarkVisibleIds = sanitizeIdList(candidate.bookmarkVisibleIds);

        return {
            ...base,
            type: 'bookmark',
            bookmarkId: candidate.bookmarkId,
            measurementVisibleIds,
            bookmarkVisibleIds
        };
    }

    if (candidate.type === 'customView') {
        if (!candidate.cameraPose || typeof candidate.cameraPose !== 'object') {
            return null;
        }

        const cameraPose = candidate.cameraPose as Record<string, unknown>;
        const position = sanitizeVec3Tuple(cameraPose.position);
        const target = sanitizeVec3Tuple(cameraPose.target);

        if (!position || !target) {
            return null;
        }

        const step: TourStepCustomView = {
            ...base,
            type: 'customView',
            cameraPose: {
                position,
                target
            }
        };

        const measurementVisibleIds = sanitizeIdList(candidate.measurementVisibleIds);
        const bookmarkVisibleIds = sanitizeIdList(candidate.bookmarkVisibleIds);
        if (measurementVisibleIds !== undefined) {
            step.measurementVisibleIds = measurementVisibleIds;
        }
        if (bookmarkVisibleIds !== undefined) {
            step.bookmarkVisibleIds = bookmarkVisibleIds;
        }

        if (isFiniteNumber(cameraPose.fov)) {
            step.cameraPose.fov = cameraPose.fov;
        }

        return step;
    }

    return null;
};

const cloneTourStep = (step: TourStep): TourStep => {
    const base = {
        transitionDuration: step.transitionDuration,
        pauseDuration: step.pauseDuration,
        layerStateOverride: step.layerStateOverride ? { ...step.layerStateOverride } : undefined,
        label: step.label,
        narrationNote: step.narrationNote,
        measurementVisibleIds: step.measurementVisibleIds ? step.measurementVisibleIds.slice() : undefined,
        bookmarkVisibleIds: step.bookmarkVisibleIds ? step.bookmarkVisibleIds.slice() : undefined
    };

    if (step.type === 'bookmark') {
        return {
            ...base,
            type: 'bookmark',
            bookmarkId: step.bookmarkId
        };
    }

    return {
        ...base,
        type: 'customView',
        cameraPose: {
            position: [step.cameraPose.position[0], step.cameraPose.position[1], step.cameraPose.position[2]],
            target: [step.cameraPose.target[0], step.cameraPose.target[1], step.cameraPose.target[2]],
            fov: step.cameraPose.fov
        }
    };
};

const sanitizeTour = (value: unknown): Tour | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    if (typeof candidate.id !== 'string' || candidate.id.length === 0) {
        return null;
    }
    if (typeof candidate.name !== 'string') {
        return null;
    }
    if (typeof candidate.createdAt !== 'string') {
        return null;
    }
    if (!Array.isArray(candidate.steps)) {
        return null;
    }

    const steps: TourStep[] = [];
    for (const item of candidate.steps) {
        const step = sanitizeTourStep(item);
        if (!step) {
            return null;
        }
        steps.push(step);
    }

    const tour: Tour = {
        id: candidate.id,
        name: candidate.name,
        steps,
        createdAt: candidate.createdAt
    };

    if (typeof candidate.author === 'string') {
        tour.author = candidate.author;
    }

    return tour;
};

const sanitizeTourPatch = (value: unknown): Partial<Omit<Tour, 'id'>> | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const patch = value as Record<string, unknown>;
    const sanitized: Partial<Omit<Tour, 'id'>> = {};

    if (typeof patch.name === 'string') {
        sanitized.name = patch.name;
    }

    if (typeof patch.createdAt === 'string') {
        sanitized.createdAt = patch.createdAt;
    }

    if (typeof patch.author === 'string') {
        sanitized.author = patch.author;
    }

    if (Array.isArray(patch.steps)) {
        const nextSteps: TourStep[] = [];
        for (const item of patch.steps) {
            const step = sanitizeTourStep(item);
            if (!step) {
                return null;
            }
            nextSteps.push(step);
        }
        sanitized.steps = nextSteps;
    }

    return sanitized;
};

const cloneTour = (tour: Tour): Tour => {
    return {
        ...tour,
        steps: tour.steps.map(cloneTourStep)
    };
};

const registerToursEvents = (events: Events) => {
    let tours: Tour[] = [];

    const listTours = () => tours.map(cloneTour);

    const fireListChanged = () => {
        events.fire('tours.listChanged', listTours());
    };

    events.function('tours.list', () => listTours());

    events.on('tours.add', (value: unknown) => {
        const stamped = value && typeof value === 'object' ? withProvenance(events, value as { author?: string; createdAt?: string }) : value;
        const tour = sanitizeTour(stamped);
        if (!tour) {
            return;
        }

        if (tours.some(item => item.id === tour.id)) {
            return;
        }

        tours = tours.concat(cloneTour(tour));
        fireListChanged();
    });

    events.on('tours.remove', (id: string) => {
        if (typeof id !== 'string') {
            return;
        }

        const next = tours.filter(item => item.id !== id);
        if (next.length === tours.length) {
            return;
        }

        tours = next;
        fireListChanged();
    });

    events.on('tours.update', (payload: TourUpdatePayload) => {
        const id = payload?.id;
        const patch = payload?.patch;

        if (typeof id !== 'string') {
            return;
        }

        const sanitizedPatch = sanitizeTourPatch(patch);
        if (!sanitizedPatch) {
            return;
        }

        let changed = false;
        tours = tours.map((tour) => {
            if (tour.id !== id) {
                return tour;
            }

            const merged = sanitizeTour({
                ...tour,
                ...sanitizedPatch,
                id: tour.id,
                steps: sanitizedPatch.steps ?? tour.steps
            });

            if (!merged) {
                return tour;
            }

            changed = true;
            return cloneTour(merged);
        });

        if (changed) {
            fireListChanged();
        }
    });

    events.on('tours.addStep', (tourId: string, step: unknown) => {
        if (typeof tourId !== 'string') {
            return;
        }

        const sanitizedStep = sanitizeTourStep(step);
        if (!sanitizedStep) {
            return;
        }

        let changed = false;
        tours = tours.map((tour) => {
            if (tour.id !== tourId) {
                return tour;
            }

            changed = true;
            return {
                ...tour,
                steps: tour.steps.concat(cloneTourStep(sanitizedStep))
            };
        });

        if (changed) {
            fireListChanged();
        }
    });

    events.on('tours.removeStep', (tourId: string, stepIndex: number) => {
        if (typeof tourId !== 'string' || !Number.isInteger(stepIndex) || stepIndex < 0) {
            return;
        }

        let changed = false;
        tours = tours.map((tour) => {
            if (tour.id !== tourId || stepIndex >= tour.steps.length) {
                return tour;
            }

            changed = true;
            const next = tour.steps.slice();
            next.splice(stepIndex, 1);
            return {
                ...tour,
                steps: next
            };
        });

        if (changed) {
            fireListChanged();
        }
    });

    events.on('tours.reorderSteps', (tourId: string, orderedIndices: number[]) => {
        if (typeof tourId !== 'string' || !Array.isArray(orderedIndices)) {
            return;
        }

        let changed = false;
        tours = tours.map((tour) => {
            if (tour.id !== tourId || tour.steps.length <= 1) {
                return tour;
            }

            const seen = new Set<number>();
            const orderedUnique: number[] = [];
            for (const index of orderedIndices) {
                if (!Number.isInteger(index) || index < 0 || index >= tour.steps.length || seen.has(index)) {
                    continue;
                }
                seen.add(index);
                orderedUnique.push(index);
            }

            const ordered = orderedUnique.map(index => tour.steps[index]);
            const remainder = tour.steps.filter((_, index) => !seen.has(index));
            const next = ordered.concat(remainder);

            const didChange = next.some((step, index) => {
                return step !== tour.steps[index];
            });

            if (!didChange) {
                return tour;
            }

            changed = true;
            return {
                ...tour,
                steps: next.map(cloneTourStep)
            };
        });

        if (changed) {
            fireListChanged();
        }
    });

    events.on('tours.setAll', (next: unknown) => {
        if (!Array.isArray(next)) {
            tours = [];
            fireListChanged();
            return;
        }

        const seenIds = new Set<string>();
        const sanitized: Tour[] = [];
        for (const value of next) {
            const tour = sanitizeTour(value);
            if (!tour || seenIds.has(tour.id)) {
                continue;
            }

            seenIds.add(tour.id);
            sanitized.push(cloneTour(tour));
        }

        tours = sanitized;
        fireListChanged();
    });

    events.on('scene.clear', () => {
        tours = [];
        fireListChanged();
    });

    fireListChanged();
};

export { registerToursEvents };
export type { Tour, TourStep, TourStepBookmark, TourStepCustomView };
